import { Injectable } from '@angular/core';
import { Entitlement, FeatureFlags, PermissionActions, User } from '../shared/models';
import { Store } from '@ngrx/store';
import * as fromRoot from '../reducers';
import * as AuthActions from '../actions/auth.actions';
import { ApiService } from './api.service';
import { LoggerService } from './logger.service';

@Injectable()
export class UserService {
  public user: User;
  public MAX_USERS = 299;
  public MIN_USERS = 3;
  private lastCheckTime: number;
  private initialized = false;
  private authenticated = false;
  public DEFAULT_ROLE = 'Default User';
  constructor(
    private api: ApiService,
    private store: Store<fromRoot.State>,
    private log: LoggerService,
  ) {
    this.init();
  }

  public init(): void {
    this.store.select(fromRoot.getAuthState).subscribe((data) => {
      this.authenticated = data.authenticated;
      if (!data.authenticated) {
        this.user = new User();
        this.initialized = false;
        return;
      }
      if (data.user) {
        this.user = data.user;
        this.initialized = true;
      }
    });
  }

  // ----- ng1 functions
  public get(key): any {
    return this.user[key];
  }

  public set(key, val): void {
    console.error('UserService.set() ERROR SETTING ' + key + ' = ' + val);
  }

  public isAuthenticated(): boolean {
    return this.authenticated;
  }

  public checkPermission(permName: PermissionActions): boolean {
    if (this.user.is_multi_new) {
      // CNC users
      const val: boolean | number = this.user.permissions[permName];
      if (typeof val === 'number') {
        return true;
        // return default true because the value is of type number
        // which means the prop is enforced
        // if boolean, it is explicit
      }
      return this.user.permissions[permName] as boolean;
    } else if (!this.user.is_multi_child && !this.user.is_multi_admin) {
      // free users
      if (
        permName === PermissionActions.VIEW_BILLING ||
        permName === PermissionActions.VIEW_USERS ||
        permName === PermissionActions.VIEW_ROLES ||
        permName === PermissionActions.SHARE_LINK_EXPIRY ||
        permName === PermissionActions.SHARE_LINK_PASSWORD ||
        permName === PermissionActions.DISABLE_EMAIL_CHANGE
      ) {
        return false;
      }
      return true;
    } else if (!this.user.is_multi_child) {
      // paid users other than teams+ will have these false as default
      if (
        permName === PermissionActions.SHARE_LINK_EXPIRY ||
        permName === PermissionActions.SHARE_LINK_PASSWORD ||
        permName === PermissionActions.DISABLE_EMAIL_CHANGE
      ) {
        return false;
      }
      if (!this.user.is_multi_admin) {
        // solo or personal paid users
        // if (permName === 'viewUsers') return false;
        return true;
      }
      // legacy business teams users
      // return (this.user.zuora_acct_key !== "" && this.user.zuora_acct_key !== null) && this.user.is_multi_admin;
      // for trial users no need to check for zuora accnt key
      if (permName === PermissionActions.VIEW_USERS) {
        return this.user.is_multi_admin;
      }
      if (permName === PermissionActions.VIEW_BILLING || permName === PermissionActions.MANAGE_BILLING) {
        return this.user.is_multi_admin;
      }
    }
    return false;
  }

  public getSubPermissionValues(permName: PermissionActions): number {
    return this.user.permissions[permName] as number;
  }

  public getPermssionByKey(permissionKey: PermissionActions): boolean | number {
    return this.user.is_multi_new == true ? this.user['permissions'][permissionKey] : true;
  }

  public clear(): void {
    console.error('UserService.clear() not implemented');
  }
  // -- end ng1 functions

  public getUser(): User {
    if (!this.initialized) {
      this.init();
    }
    return this.user;
  }

  public getStarted(): any {
    const getstarted = {
      hasVerified: this.user.is_verified,
      hasPasswordHint: this.user.has_password_hint,
      hasMobile: this.user.install_mobile,
      hasDeviceOne: this.user.install_desktop,
      hasDeviceMany: this.user.install_desktop > 1,
      hasFiles: this.user.has_files,
      hasShares: this.user.has_shares,
      hasLinks: this.user.has_links,
      getstarted_completed: 0,
    };
    return getstarted;
  }

  /**
   * Initiates a refresh request for the user.  This will trigger the subscription.
   * The auth.effects and reducer will take care of updating the state of the user object.
   */
  public refresh(): void {
    this.lastCheckTime = Date.now();
    this.store.dispatch(new AuthActions.RefreshAction());
  }

  /**
   * Updates every 5 minutes, used for any polling.
   */
  public update(): void {
    const FIVE_MIN = 5 * 60 * 1000;
    if (!this.lastCheckTime || Date.now() - this.lastCheckTime > FIVE_MIN) {
      this.refresh();
    }
  }

  /**
   * Initiates the network call to get the actual user information.
   */
  public async reload(): Promise<any> {
    // check if user is authenticated
    if (this.user.exists === 1) {
      this.lastCheckTime = Date.now();
      return await this.api.execute('userget', {});
    }
  }

  public dateConversionForTrials(termEndDate: string, trimOneDay?: boolean): Date {
    const dateArr = termEndDate.split('-');
    if (dateArr.length !== 3) {
      this.log.error(`An invalid term end date received ${termEndDate}`);
    }
    const d = parseInt(dateArr[2], 10) - (trimOneDay ? 1 : 0),
      // because the data that we have here is related to subscription and not the actual plan term
      // as per zuora, if auto renew is enabled, the subscription current term end and new renewal
      // happens on same day, even though the plan term ends a day before
      m = parseInt(dateArr[1], 10) - 1,
      // no need to trim month as we are using date string
      y = parseInt(dateArr[0], 10);
    return new Date(y, m, d);
    // zuora date format changed from yyyy-mm-dd to mm-dd-yyyy so that when converted to user's local time zone,
    // the utc time will be considered and time zone issue will be resolved.
    // This time zone fix is being applied only for trial users
  }

  public setUserInfo = async (parameters: { [key: string]: any }): Promise<void> => {
    await this.api.execute('userinfoset', parameters);
  };

  public isFeatureAllowed(entitlement: string, value?: string): boolean {
    value = value || 'true';
    return this.user?.entitlements?.[entitlement]?.value == value;
  }

  public isFeatureLimitAllowed(entitlement: string, value: number): boolean {
    const entitlementObj = this.getEntitlement(entitlement);
    if (!entitlementObj.is_unlimited) {
      return value <= +entitlementObj.value;
    }
    return true;
  }

  public getEntitlement(entitlement: string): Entitlement {
    return this.user?.entitlements?.[entitlement];
  }

  public isRedirectToUpgradePlan(): boolean {
    const isChild = this.user.is_multi_child && !this.user.is_multi_admin;
    return !!(
      (this.checkPermission(PermissionActions.MANAGE_BILLING) &&
        !isChild &&
        !this.user.is_pro &&
        !this.user.is_on_trial) ||
      this.user.is_free_plan
    );
  }

  public showUpgradeButton(): boolean {
    const isChild = this.user.is_multi_child && !this.user.is_multi_admin;
    return !this.user.is_unlimited_plan && !isChild && !this.user.is_on_trial;
  }

  // privacy_settings flags are opt-out on the server: a field is only ever 0 when the user has explicitly
  // disabled the feature; missing or undefined means "allowed" (matches the server-side default). The
  // explicit `!== 0` shape is intentional so a missing field defaults to allow rather than block.
  public isMSOfficeEditAllowed(): boolean {
    return this.user.privacy_settings?.microsoft_editor_allowed !== 0;
  }

  public isDownloadAcceleratorAllowed(): boolean {
    return this.user.privacy_settings?.download_accelerator_allowed !== 0;
  }

  public isThumbnailsAllowed(): boolean {
    return this.user.privacy_settings?.extended_web_thumbnails_allowed !== 0;
  }

  /**
   * PostHog feature flag check
   */
  public hasFeatureFlag(flag: FeatureFlags): boolean {
    return !!this.user?.feature_flags?.[flag]?.enabled;
  }

  /**
   * Returns the payload attached to a PostHog feature flag, or null if unavailable.
   */
  public getFeatureFlagPayload(flag: FeatureFlags): Record<string, any> | null {
    return this.user?.feature_flags?.[flag]?.payload ?? null;
  }

  /**
   * Teams+ accounts can toggle email-based password reset on/off only once the
   * CP_ALLOW_DISABLE_PASSWORD_RESET flag is enabled for them
   * non-Teams users are unaffected.
   */
  public canToggleTeamsPasswordReset(): boolean {
    return !!this.user?.is_multi_new && this.hasFeatureFlag(FeatureFlags.ALLOW_DISABLE_PASSWORD_RESET);
  }
}
